import { Component, OnInit } from "@angular/core";
import {
    FormBuilder,
    FormGroup,
    Validators,
    ReactiveFormsModule,
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatIconModule } from "@angular/material/icon";

import { AuthService } from "../../../core/services/auth.service";
import { ResetPasswordRequest } from "../../../core/models/auth.model";

@Component({
    selector: "app-reset-password",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterLink,
        MatButtonModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatIconModule,
    ],
    templateUrl: "./reset-password.html",
    styleUrls: ["./reset-password.scss"],
})
export class ResetPasswordComponent implements OnInit {
    resetPasswordForm: FormGroup;
    error: string = "";
    loading: boolean = false;
    currentYear: number = new Date().getFullYear();
    showSuccessMessage: boolean = false;
    token: string = "";
    hidePassword = true;
    hideConfirmPassword = true;

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        this.resetPasswordForm = this.fb.group(
            {
                password: [
                    "",
                    [
                        Validators.required,
                        Validators.minLength(8),
                        Validators.maxLength(128),
                        this.passwordComplexityValidator(),
                    ],
                ],
                confirmPassword: ["", [Validators.required]],
            },
            { validators: this.passwordMatchValidator }
        );
    }

    passwordComplexityValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) return null;

            const hasUpperCase = /[A-Z]/.test(value);
            const hasNumber = /[0-9]/.test(value);
            const hasSymbol = /[^A-Za-z0-9]/.test(value);

            const passwordValid = hasUpperCase && hasNumber && hasSymbol;

            if (!passwordValid) {
                return {
                    complexity: {
                        hasUpperCase,
                        hasNumber,
                        hasSymbol,
                    },
                };
            }
            return null;
        };
    }

    passwordMatchValidator: ValidatorFn = (
        group: AbstractControl
    ): ValidationErrors | null => {
        const password = group.get("password")?.value;
        const confirmPassword = group.get("confirmPassword")?.value;
        const confirmControl = group.get("confirmPassword");

        if (password !== confirmPassword) {
            confirmControl?.setErrors({ ...confirmControl?.errors, mismatch: true });
            return { mismatch: true };
        } else {
            if (confirmControl?.hasError("mismatch")) {
                const errors = { ...confirmControl.errors };
                delete errors["mismatch"];
                confirmControl.setErrors(Object.keys(errors).length ? errors : null);
            }
            return null;
        }
    };

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            const tokenParam = params["token"];
            if (tokenParam) {
                this.token = tokenParam;
            } else {
                this.error = "Reset token is missing. Please use the link from your email.";
            }
        });
    }

    onSubmit(): void {
        if (this.resetPasswordForm.valid && this.token) {
            this.loading = true;
            this.error = "";

            const request: ResetPasswordRequest = {
                token: this.token,
                password: this.resetPasswordForm.value.password,
            };

            this.authService.resetPassword(request).subscribe({
                next: () => {
                    this.showSuccessMessage = true;
                    this.loading = false;
                },
                error: (err) => {
                    this.error =
                        err.message ||
                        "Failed to reset password. The link may have expired or is invalid.";
                    this.loading = false;
                },
            });
        }
    }

    goToLogin(): void {
        this.router.navigate(["/login"]);
    }
}
